/**
 * Unified inline spell + grammar TipTap extension.
 *
 * One LT-backed check produces matches with `kind: "spell" | "grammar"`.
 * Each kind has its own toggle (`spellEnabled`, `grammarEnabled`) and is
 * rendered with its own decoration class (`spell-error` / `grammar-error`).
 *
 * The user-facing toolbar has two buttons (Spell Check, Grammar Check) that
 * flip those two flags independently — but both kinds share a single backend
 * request and a single popover.
 *
 * Filtering architecture: the server is now a thin LT proxy with no Prisma
 * reads. The user's dictionary + style rules are loaded once via
 * GET /api/grammar/preferences (the first time the toggle turns on),
 * cached in `editor.storage.grammar.preferences`, and combined with the
 * Story Bible character names already held in the client's Zustand store.
 * After every LT response we run `filterMatches()` locally before turning
 * matches into decorations.
 */

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { useStoryBibleStore } from "../../stores/storyBibleStore";
import { extractTextFromTipTap } from "../../../../src/shared/schemas/CharacterSchema";
import {
  extractText,
  extractParagraphAt,
  plainWindowToPmRange,
  matchesToDecorationRanges,
  checkGrammar,
  checkGrammarProgressively,
  checkGrammarWithFullDocumentCache,
  fetchGrammarPreferences,
  remapRanges,
  filterMatches,
  buildGrammarCheckCacheKey,
  expandNameTokens,
  tokenizeOtherNames,
  type GrammarMatch,
  type GrammarCheckOptions,
  type GrammarCheckSource,
  type GrammarPreferences,
  type UserStyleRule,
  type FullGrammarCheckCacheEntry,
  type FullGrammarCheckInFlight,
  getDocumentChangeStats,
} from "./grammarEngine";
import {
  GRAMMAR_SUGGESTION_CANCEL_CLOSE_EVENT,
  GRAMMAR_SUGGESTION_CLOSE_EVENT,
  GRAMMAR_SUGGESTION_OPEN_EVENT,
} from "./grammarEvents";

export const grammarPluginKey = new PluginKey("grammarPlugin");
export const GRAMMAR_HOVER_OPEN_DELAY_MS = 200;
/** Debounce before typing-triggered /api/grammar/check requests fire. */
export const GRAMMAR_TYPING_DEBOUNCE_MS = 1000;

const FULL_DOC_PREVIEW_MIN_CHARS = 12000;

interface DecorationRange {
  from: number;
  to: number;
  match: GrammarMatch;
}

export function findGrammarRangeForPosition(
  ranges: DecorationRange[],
  pos: number,
  ruleId: string,
  exactFrom?: number | null,
  exactTo?: number | null
): DecorationRange | null {
  if (exactFrom != null && exactTo != null) {
    const exactRange = ranges.find(
      (r: DecorationRange) =>
        r.match.ruleId === ruleId &&
        r.from === exactFrom &&
        r.to === exactTo
    );
    if (exactRange) return exactRange;
  }

  return (
    ranges.find(
      (r: DecorationRange) =>
        r.match.ruleId === ruleId && pos >= r.from && pos <= r.to
    ) ?? null
  );
}

export function getGrammarSuggestionAnchorPosition(
  eventName: string,
  pos: number,
  range: Pick<DecorationRange, "from" | "to">
): number {
  if (eventName !== "suggestion-click") return range.from;
  return Math.min(Math.max(pos, range.from), range.to);
}

function rectForElement(element: HTMLElement | null | undefined) {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  if (
    !Number.isFinite(rect.left) ||
    !Number.isFinite(rect.top) ||
    !Number.isFinite(rect.width) ||
    !Number.isFinite(rect.height)
  ) {
    return null;
  }
  return {
    x: rect.left,
    y: rect.top,
    width: Math.max(1, rect.width),
    height: Math.max(1, rect.height),
  };
}

function rectForGrammarRange(
  view: any,
  range: Pick<DecorationRange, "from" | "to">,
  fallbackPos: number
) {
  const from = Number.isFinite(range.from) ? range.from : fallbackPos;
  const to = Number.isFinite(range.to) ? range.to : fallbackPos;
  try {
    const start = view.coordsAtPos(Math.min(from, to));
    const end = view.coordsAtPos(Math.max(from, to));
    const left = Math.min(start.left, end.left);
    const right = Math.max(start.left, end.left);
    const top = Math.min(start.top ?? start.bottom, end.top ?? end.bottom);
    const bottom = Math.max(start.bottom ?? start.top, end.bottom ?? end.top);
    if (
      !Number.isFinite(left) ||
      !Number.isFinite(top) ||
      !Number.isFinite(right) ||
      !Number.isFinite(bottom)
    ) {
      return null;
    }
    return {
      x: left,
      y: top,
      width: Math.max(1, right - left),
      height: Math.max(1, bottom - top),
    };
  } catch {
    return null;
  }
}

export function removeRangesTouchedByDocumentChange<
  T extends Pick<DecorationRange, "from" | "to">
>(ranges: T[], mapping: any): T[] {
  const maps = Array.isArray(mapping?.maps) ? mapping.maps : [];
  if (maps.length === 0) return ranges;

  return ranges.filter((range) => {
    const touched = maps.some((stepMap) => {
      if (typeof stepMap?.forEach !== "function") return false;
      let stepTouched = false;
      stepMap.forEach((oldStart: number, oldEnd: number) => {
        const isInsertion = oldStart === oldEnd;
        const insertedWithinRange =
          isInsertion && oldStart >= range.from && oldStart < range.to;
        const replacedRangeOverlaps =
          !isInsertion && oldStart < range.to && oldEnd > range.from;
        if (insertedWithinRange || replacedRangeOverlaps) {
          stepTouched = true;
        }
      });
      return stepTouched;
    });
    return !touched;
  });
}

export function createDelayedGrammarHoverOpen<TArgs extends unknown[]>(
  open: (...args: TArgs) => void,
  delayMs = GRAMMAR_HOVER_OPEN_DELAY_MS
) {
  let hoverOpenTimeout: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (hoverOpenTimeout) {
      clearTimeout(hoverOpenTimeout);
      hoverOpenTimeout = null;
    }
  };

  const schedule = (...args: TArgs) => {
    cancel();
    hoverOpenTimeout = setTimeout(() => {
      hoverOpenTimeout = null;
      try {
        open(...args);
      } catch {
        // Hover-open is best-effort; stale editor views can disappear while the delay is pending.
      }
    }, delayMs);
  };

  return { schedule, cancel };
}

export function getDocumentChangeGrammarCheckPlan({
  cursorPos,
  insertedChars,
  largestInsertionChars,
}: {
  cursorPos: number | null;
  insertedChars: number;
  largestInsertionChars: number;
}): { mode: "full" | "paragraph"; source: GrammarCheckSource } {
  const shouldRunFullCheck =
    insertedChars >= FULL_DOC_PREVIEW_MIN_CHARS ||
    largestInsertionChars >= FULL_DOC_PREVIEW_MIN_CHARS;
  if (shouldRunFullCheck) {
    return { mode: "full", source: "paste" };
  }
  if (cursorPos == null) {
    return { mode: "full", source: "edit_fallback" };
  }
  return { mode: "paragraph", source: "typing" };
}

function classFor(kind: "spell" | "grammar"): string {
  return kind === "spell" ? "spell-error" : "grammar-error";
}

function buildDecorations(
  doc: any,
  ranges: DecorationRange[],
  spellEnabled: boolean,
  grammarEnabled: boolean
): DecorationSet {
  if (ranges.length === 0) return DecorationSet.empty;

  const decorations: Decoration[] = [];
  for (const r of ranges) {
    const kind = r.match.kind === "spell" ? "spell" : "grammar";
    if (kind === "spell" && !spellEnabled) continue;
    if (kind === "grammar" && !grammarEnabled) continue;
    decorations.push(
      Decoration.inline(r.from, r.to, {
        class: classFor(kind),
        "data-grammar-rule-id": r.match.ruleId,
        "data-grammar-from": String(r.from),
        "data-grammar-to": String(r.to),
        "data-suggestion-kind": kind,
      })
    );
  }

  if (decorations.length === 0) return DecorationSet.empty;
  return DecorationSet.create(doc, decorations);
}

/**
 * Collect every character-name token currently in the Story Bible. Names
 * live as TipTap JSON on the client (`character.name`, `character.otherNames`)
 * and need flattening + multi-word splitting so LT's single-word matches
 * are silenced. Defensive against the store being unavailable in tests.
 */
function collectStoryBibleNameTokens(): string[] {
  try {
    const entities = useStoryBibleStore.getState().charactersEntities ?? [];
    const tokens: string[] = [];
    for (const c of entities) {
      if (!c) continue;
      const namePlain =
        c.displayName ?? (c.name != null ? extractTextFromTipTap(c.name) : "");
      for (const t of expandNameTokens(namePlain)) tokens.push(t);
      const otherNamesPlain =
        c.otherNames != null ? extractTextFromTipTap(c.otherNames) : "";
      for (const alias of tokenizeOtherNames(otherNamesPlain)) {
        for (const t of expandNameTokens(alias)) tokens.push(t);
      }
    }
    const wbEntries = useStoryBibleStore.getState().worldbuildingEntries ?? [];
    for (const wb of wbEntries) {
      if (!wb) continue;
      const namePlain =
        wb.name != null ? extractTextFromTipTap(wb.name) : "";
      for (const t of expandNameTokens(namePlain)) tokens.push(t);
      const otherNamesPlain =
        wb.otherNames != null ? extractTextFromTipTap(wb.otherNames) : "";
      for (const alias of tokenizeOtherNames(otherNamesPlain)) {
        for (const t of expandNameTokens(alias)) tokens.push(t);
      }
    }

    return tokens;
  } catch {
    return [];
  }
}

interface GrammarStorage {
  spellEnabled: boolean;
  grammarEnabled: boolean;
  checksSuppressed: boolean;
  suppressionReason: string | null;
  dirtyWhileSuppressed: boolean;
  ranges: DecorationRange[];
  pendingCheck: ReturnType<typeof setTimeout> | null;
  requestSeq: number;
  documentId: string | null;
  /** SW-9064: language passed to LT. Pushed in by TiptapDocumentSB. */
  languagePreference: string;
  preferences: GrammarPreferences;
  preferencesLoadedAt: number | null;
  preferencesInFlight: Promise<GrammarPreferences> | null;
  fullCheckCache: FullGrammarCheckCacheEntry | null;
  fullCheckInFlight: FullGrammarCheckInFlight | null;
  streamAbort: AbortController | null;
  checkAbort: AbortController | null;
}

function abortGrammarRequests(storage: GrammarStorage) {
  storage.streamAbort?.abort();
  storage.streamAbort = null;
  storage.checkAbort?.abort();
  storage.checkAbort = null;
}

export const GrammarExtension = Extension.create({
  name: "grammar",

  addStorage(): GrammarStorage {
    return {
      spellEnabled: false,
      grammarEnabled: false,
      checksSuppressed: false,
      suppressionReason: null,
      dirtyWhileSuppressed: false,
      ranges: [],
      pendingCheck: null,
      requestSeq: 0,
      documentId: null,
      languagePreference: "en-US",
      preferences: { dictionary: [], styleRules: [] },
      preferencesLoadedAt: null,
      preferencesInFlight: null,
      fullCheckCache: null,
      fullCheckInFlight: null,
      streamAbort: null,
      checkAbort: null,
    };
  },

  addCommands() {
    return {
      setSpellEnabled:
        (enabled: boolean) =>
        ({ editor, tr }: any) => {
          editor.storage.grammar.spellEnabled = enabled;
          tr.setMeta(grammarPluginKey, { type: "toggle-kind", kind: "spell", enabled });
          return true;
        },

      /**
       * SW-9064: pushed in by TiptapDocumentSB whenever
       * UserData.languagePreference changes. Triggers a full recheck so
       * en-US -> en-GB underlines update across the doc.
       */
      setLanguagePreference:
        (language: string) =>
        ({ editor }: any) => {
          const next = (language || "en-US").trim() || "en-US";
          if (editor.storage.grammar.languagePreference === next) return false;
          editor.storage.grammar.languagePreference = next;
          // Immediate recheck — the typing debounce makes a language
          // switch feel laggy ("did anything happen?") so we bypass it
          // for this explicit user action.
          if (editor.commands.recheckGrammarFull) {
            editor.commands.recheckGrammarFull(true);
          }
          return true;
        },

      setGrammarEnabled:
        (enabled: boolean) =>
        ({ editor, tr }: any) => {
          editor.storage.grammar.grammarEnabled = enabled;
          tr.setMeta(grammarPluginKey, { type: "toggle-kind", kind: "grammar", enabled });
          return true;
        },

      /**
       * Combined toggle for the unified "Spellcheck" toolbar button: flips
       * both spell and grammar in a single transaction so only one recheck
       * is scheduled by the plugin's `apply()` (vs. two when calling
       * `setSpellEnabled` then `setGrammarEnabled`).
       *
       * The individual `setSpellEnabled` / `setGrammarEnabled` commands are
       * kept for back-compat: the mount-time sync in TiptapDocumentSB and
       * the popover's dismiss flow still call them directly.
       */
      setSpellcheckEnabled:
        (enabled: boolean) =>
        ({ editor, tr }: any) => {
          editor.storage.grammar.spellEnabled = enabled;
          editor.storage.grammar.grammarEnabled = enabled;
          tr.setMeta(grammarPluginKey, { type: "toggle-kind", enabled });
          return true;
        },

      setGrammarChecksSuppressed:
        (suppressed: boolean, reason: string = "manual") =>
        ({ editor, tr }: any) => {
          const storage = editor.storage.grammar as GrammarStorage;
          const shouldRecheck = !suppressed && !!storage.dirtyWhileSuppressed;

          storage.checksSuppressed = suppressed;
          storage.suppressionReason = suppressed ? reason : null;
          if (suppressed) {
            abortGrammarRequests(storage);
            storage.requestSeq += 1;
            if (storage.pendingCheck) {
              clearTimeout(storage.pendingCheck);
              storage.pendingCheck = null;
            }
          }
          if (!suppressed) {
            storage.dirtyWhileSuppressed = false;
          }

          tr.setMeta(grammarPluginKey, {
            type: "checks-suppressed",
            suppressed,
            reason,
            shouldRecheck,
          });
          return true;
        },

      setGrammarDocumentId:
        (documentId: string | null) =>
        ({ editor }: any) => {
          editor.storage.grammar.documentId = documentId;
          return true;
        },

      replaceMatch:
        (from: number, to: number, replacement: string) =>
        ({ tr, dispatch }: any) => {
          const maxPosition = tr.doc.content.size;
          if (
            !Number.isFinite(from) ||
            !Number.isFinite(to) ||
            from < 0 ||
            to < from ||
            to > maxPosition
          ) {
            return false;
          }

          if (dispatch) tr.insertText(replacement, from, to);
          return true;
        },

      /**
       * Force a full-document recheck. Used by the popover after a
       * successful `Add to dictionary` / `Ignore` so newly-allowed words
       * and phrases lose their underlines everywhere — not just on the
       * paragraph the user is typing in.
       *
       * `immediate` skips the typing debounce. Pass `true` for
       * explicit user actions (language change, dictionary delete) so
       * the doc updates without a perceptible lag.
       */
      recheckGrammarFull:
        (immediate: boolean = false) =>
        ({ tr, dispatch }: any) => {
          if (dispatch) tr.setMeta(grammarPluginKey, { type: "recheck-full", immediate });
          return true;
        },

      /**
       * Push a word into the cached dictionary so the next /check filter
       * pass treats it as known immediately, without waiting for a
       * /preferences refetch. The popover calls this right after firing
       * POST /api/grammar/dictionary so the underline doesn't flicker
       * back on the next debounce tick.
       */
      addLocalDictionaryWord:
        (word: string) =>
        ({ editor }: any) => {
          const trimmed = (word || "").trim();
          if (!trimmed) return false;
          const pref = editor.storage.grammar.preferences as GrammarPreferences;
          if (!pref.dictionary.includes(trimmed)) {
            pref.dictionary = [...pref.dictionary, trimmed];
          }
          return true;
        },

      /**
       * SW-9064: drop a word from the cached dictionary so the next
       * /check filter pass surfaces it again. Used by Style Settings →
       * Dictionary → trash icon.
       */
      removeLocalDictionaryWord:
        (word: string) =>
        ({ editor }: any) => {
          const trimmed = (word || "").trim();
          if (!trimmed) return false;
          const pref = editor.storage.grammar.preferences as GrammarPreferences;
          pref.dictionary = pref.dictionary.filter((w) => w !== trimmed);
          return true;
        },

      /**
       * SW-9064: drop a style rule from the cache by (rule_id, match_text).
       * Used by Style Settings → Ignored rules → trash icon.
       */
      removeLocalStyleRule:
        ({ ruleId, matchText }: { ruleId: string; matchText: string | null }) =>
        ({ editor }: any) => {
          if (!ruleId) return false;
          const pref = editor.storage.grammar.preferences as GrammarPreferences;
          pref.styleRules = pref.styleRules.filter(
            (r) => !(r.rule_id === ruleId && (r.match_text ?? null) === (matchText ?? null))
          );
          return true;
        },

      /**
       * Push a (ruleId, matchText) phrase override into the cached style
       * rules so the next /check filter pass dismisses it immediately.
       */
      addLocalStyleRule:
        (rule: UserStyleRule) =>
        ({ editor }: any) => {
          if (!rule || !rule.rule_id) return false;
          const pref = editor.storage.grammar.preferences as GrammarPreferences;
          pref.styleRules = [...pref.styleRules, rule];
          return true;
        },

      /**
       * Optimistically remove the decoration at [from, to] for a given
       * ruleId. Used after a successful dismiss (Add to dictionary /
       * Ignore) so the underline disappears immediately, before the
       * backend round-trip completes.
       *
       * Note: we filter the storage `ranges` array and rebuild the
       * DecorationSet via a `recheck-complete` meta so the plugin state
       * stays the single source of truth.
       */
      dismissMatch:
        (from: number, to: number, ruleId: string) =>
        ({ editor, tr, dispatch }: any) => {
          const storage = editor.storage.grammar;
          const before = storage.ranges as DecorationRange[];
          const after = before.filter(
            (r) => !(r.from === from && r.to === to && r.match.ruleId === ruleId)
          );
          if (after.length === before.length) {
            // Nothing matched — bail out, popover already closing.
            return false;
          }
          storage.ranges = after;
          const decorations = buildDecorations(
            editor.state.doc,
            after,
            storage.spellEnabled,
            storage.grammarEnabled
          );
          if (dispatch) {
            tr.setMeta(grammarPluginKey, {
              type: "recheck-complete",
              decorations,
            });
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const DEBOUNCE_MS = GRAMMAR_TYPING_DEBOUNCE_MS;
    const hoverOpen = createDelayedGrammarHoverOpen(
      (view: any, pos: number, mark: HTMLElement) => {
        dispatchSuggestionOpen(view, pos, GRAMMAR_SUGGESTION_OPEN_EVENT, mark);
      }
    );

    return [
      new Plugin({
        key: grammarPluginKey,

        state: {
          init() {
            return DecorationSet.empty;
          },

          apply(tr, value, _oldState, newState) {
            const meta = tr.getMeta(grammarPluginKey);
            const { spellEnabled, grammarEnabled } = editor.storage.grammar;
            const anyEnabled = spellEnabled || grammarEnabled;

            if (meta?.type === "toggle-kind") {
              if (!anyEnabled) {
                // Both off: clear decorations and cancel any pending check.
                abortGrammarRequests(editor.storage.grammar);
                if (editor.storage.grammar.pendingCheck) {
                  clearTimeout(editor.storage.grammar.pendingCheck);
                  editor.storage.grammar.pendingCheck = null;
                }
                editor.storage.grammar.ranges = [];
                return DecorationSet.empty;
              }
              // Rebuild from cached ranges so toggling one kind ON/OFF
              // re-renders without making a network call. If we don't have
              // any cached ranges yet (first time enabling), schedule a
              // full check — this covers page load, doc switch, and the
              // very first toggle-on.
              if (
                editor.storage.grammar.ranges.length === 0 &&
                !editor.isDestroyed
              ) {
                if (editor.storage.grammar.checksSuppressed) {
                  editor.storage.grammar.dirtyWhileSuppressed = true;
                  return value;
                }
                scheduleCheck("full", null, false, "toggle_enabled");
                return value;
              }
              return buildDecorations(
                newState.doc,
                editor.storage.grammar.ranges,
                spellEnabled,
                grammarEnabled
              );
            }

            if (meta?.type === "recheck-complete") {
              return meta.decorations as DecorationSet;
            }

            if (meta?.type === "recheck-full") {
              if (anyEnabled && !editor.isDestroyed) {
                if (editor.storage.grammar.checksSuppressed) {
                  editor.storage.grammar.dirtyWhileSuppressed = true;
                  return value;
                }
                scheduleCheck("full", null, !!meta.immediate, "manual_recheck");
              }
              return value;
            }

            if (meta?.type === "checks-suppressed") {
              if (meta.suppressed) {
                if (editor.storage.grammar.pendingCheck) {
                  clearTimeout(editor.storage.grammar.pendingCheck);
                  editor.storage.grammar.pendingCheck = null;
                }
                return value;
              }

              if (meta.shouldRecheck && anyEnabled && !editor.isDestroyed) {
                scheduleCheck(
                  "full",
                  null,
                  true,
                  meta.reason === "draft_stream"
                    ? "draft_stream_complete"
                    : "suppression_released"
                );
              }
              return value;
            }

            if (tr.docChanged && anyEnabled) {
              // Drop any underline the user is actively editing, then keep
              // untouched ranges synced with the new doc positions. The next
              // grammar check is the source of truth for whether the edited
              // word still needs an underline.
              const previousRanges = editor.storage.grammar.ranges;
              const untouchedRanges = removeRangesTouchedByDocumentChange(
                previousRanges,
                tr.mapping
              );
              const remappedRanges = remapRanges(untouchedRanges, tr.mapping);
              editor.storage.grammar.ranges = remappedRanges;
              const removedRange =
                untouchedRanges.length !== previousRanges.length ||
                remappedRanges.length !== untouchedRanges.length;
              const mapped = removedRange
                ? buildDecorations(
                    newState.doc,
                    remappedRanges,
                    spellEnabled,
                    grammarEnabled
                  )
                : value.map(tr.mapping, tr.doc);
              if (editor.storage.grammar.checksSuppressed) {
                editor.storage.grammar.dirtyWhileSuppressed = true;
                return mapped;
              }
              if (!editor.isDestroyed) {
                const cursorPos = newState.selection?.$head?.pos ?? null;
                const changeStats = getDocumentChangeStats(tr.mapping);
                const checkPlan = getDocumentChangeGrammarCheckPlan({
                  cursorPos,
                  insertedChars: changeStats.insertedChars,
                  largestInsertionChars: changeStats.largestInsertionChars,
                });
                scheduleCheck(
                  checkPlan.mode,
                  cursorPos,
                  false,
                  checkPlan.source
                );
              }
              return mapped;
            }

            return value;
          },
        },

        props: {
          decorations(state) {
            return this.getState(state);
          },

          handleClick(view, pos) {
            hoverOpen.cancel();
            return dispatchSuggestionOpen(view, pos, "suggestion-click");
          },

          handleDOMEvents: {
            mouseover(view, event) {
              const target = event.target as HTMLElement | null;
              const relatedTarget = event.relatedTarget as Node | null;
              const mark = target?.closest?.(".spell-error, .grammar-error") as HTMLElement | null;
              if (!mark || (relatedTarget && mark.contains(relatedTarget))) return false;

              try {
                const pos = view.posAtDOM(mark, 0);
                mark.dispatchEvent(
                  new CustomEvent(GRAMMAR_SUGGESTION_CANCEL_CLOSE_EVENT, {
                    bubbles: true,
                  })
                );
                hoverOpen.schedule(view, pos, mark);
              } catch {
                return false;
              }
              return false;
            },

            mouseout(_view, event) {
              const target = event.target as HTMLElement | null;
              const relatedTarget = event.relatedTarget as Node | null;
              const mark = target?.closest?.(".spell-error, .grammar-error") as HTMLElement | null;
              if (!mark || (relatedTarget && mark.contains(relatedTarget))) return false;

              hoverOpen.cancel();
              mark.dispatchEvent(
                new CustomEvent(GRAMMAR_SUGGESTION_CLOSE_EVENT, {
                  bubbles: true,
                })
              );
              return false;
            },
          },
        },
      }),
    ];

    function dispatchSuggestionOpen(
      view: any,
      pos: number,
      eventName: string,
      targetElement?: HTMLElement | null
    ): boolean {
      if (!view?.state || view.isDestroyed || editor.isDestroyed) return false;
      if (targetElement && !targetElement.isConnected) return false;

      const storage = editor.storage.grammar;
      if (!storage.spellEnabled && !storage.grammarEnabled) return false;

      const docSize = view.state.doc?.content?.size;
      const hasDocSize = typeof docSize === "number" && Number.isFinite(docSize);
      if (!Number.isFinite(pos) || pos < 0 || (hasDocSize && pos > docSize)) return false;

      const pluginState = grammarPluginKey.getState(view.state);
      if (!pluginState) return false;

      const findTo = hasDocSize ? Math.min(pos + 1, docSize) : pos + 1;
      const decos = pluginState.find(pos, findTo);
      const deco = decos.find((d: any) => {
        const cls: string = d.type?.attrs?.class || "";
        return cls.includes("spell-error") || cls.includes("grammar-error");
      });
      const ruleId =
        targetElement?.getAttribute("data-grammar-rule-id") ??
        deco?.type?.attrs?.["data-grammar-rule-id"];
      if (!ruleId) return false;

      const exactFrom =
        readGrammarRangePositionFromDeco(deco, "from") ??
        readGrammarRangeAttr(targetElement, "data-grammar-from") ??
        readGrammarRangeAttrFromDeco(deco, "data-grammar-from");
      const exactTo =
        readGrammarRangePositionFromDeco(deco, "to") ??
        readGrammarRangeAttr(targetElement, "data-grammar-to") ??
        readGrammarRangeAttrFromDeco(deco, "data-grammar-to");
      const range = findGrammarRangeForPosition(
        storage.ranges,
        pos,
        ruleId,
        exactFrom,
        exactTo
      );
      if (!range) return false;

      const anchorPos = getGrammarSuggestionAnchorPosition(
        eventName,
        pos,
        range
      );
      if (!Number.isFinite(anchorPos) || anchorPos < 0 || (hasDocSize && anchorPos > docSize)) return false;

      let coords;
      try {
        coords = view.coordsAtPos(anchorPos);
      } catch {
        return false;
      }
      const anchorRect =
        rectForElement(targetElement) ??
        rectForGrammarRange(view, range, anchorPos);

      const customEvent = new CustomEvent(eventName, {
        detail: {
          match: range.match,
          from: range.from,
          to: range.to,
          x: coords.left,
          y: coords.bottom,
          ...(anchorRect ? { anchorRect } : {}),
        },
        bubbles: true,
      });
      (targetElement ?? view.dom).dispatchEvent(customEvent);
      return true;
    }

    function readGrammarRangeAttr(
      element: HTMLElement | null | undefined,
      attr: string
    ): number | null {
      const value = element?.getAttribute(attr);
      if (value == null) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    function readGrammarRangeAttrFromDeco(
      deco: any,
      attr: string
    ): number | null {
      const value = deco?.type?.attrs?.[attr];
      if (value == null) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    function readGrammarRangePositionFromDeco(
      deco: any,
      attr: "from" | "to"
    ): number | null {
      const value = deco?.[attr];
      return typeof value === "number" && Number.isFinite(value) ? value : null;
    }

    /**
     * Fetch GET /api/grammar/preferences exactly once per editor mount.
     * Subsequent toggles reuse the cached result; optimistic updates
     * (Add to dictionary / Ignore) push into the cached dictionary +
     * styleRules so the next filter pass sees them immediately.
     *
     * Returns the cached preferences. On failure, returns a safe empty
     * pair and lets the check fall through — spell/grammar will just
     * not filter anything beyond the global rules sent to LT.
     */
    async function ensurePreferences(): Promise<GrammarPreferences> {
      const storage = editor.storage.grammar;
      if (storage.preferencesLoadedAt) return storage.preferences;
      if (storage.preferencesInFlight) return storage.preferencesInFlight;
      const promise = fetchGrammarPreferences()
        .then((prefs) => {
          storage.preferences = prefs;
          storage.preferencesLoadedAt = Date.now();
          return prefs;
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error("[spell+grammar] preferences load failed:", err);
          return storage.preferences;
        })
        .finally(() => {
          storage.preferencesInFlight = null;
        });
      storage.preferencesInFlight = promise;
      return promise;
    }

    function applyFullDocumentChunk({
      chunk,
      plainText,
      segments,
      preferences,
      dictionary,
      possessiveBases,
      language,
      requestSeq,
      fullCacheKey,
    }: {
      chunk: { offset: number; length: number; matches: GrammarMatch[] };
      plainText: string;
      segments: ReturnType<typeof extractText>["segments"];
      preferences: GrammarPreferences;
      dictionary: string[];
      possessiveBases: string[];
      language: string;
      requestSeq: number;
      fullCacheKey: string;
    }) {
      if (requestSeq !== editor.storage.grammar.requestSeq) return;
      if (!editor.view || editor.isDestroyed) return;
      if (editor.storage.grammar.fullCheckCache?.key === fullCacheKey) return;

      const after = editor.storage.grammar;
      if (!after.spellEnabled && !after.grammarEnabled) return;

      const pmRange = plainWindowToPmRange(
        segments,
        chunk.offset,
        chunk.offset + chunk.length
      );
      if (!pmRange) return;

      const filtered = filterMatches({
        text: plainText,
        matches: chunk.matches,
        dictionary,
        possessiveBases,
        language,
        styleRules: preferences.styleRules,
      });
      const chunkRanges = matchesToDecorationRanges(filtered, segments);
      const kept = after.ranges.filter(
        (r: DecorationRange) => r.to <= pmRange.pmStart || r.from >= pmRange.pmEnd
      );
      const mergedRanges = [...kept, ...chunkRanges];
      editor.storage.grammar.ranges = mergedRanges;

      const decorations = buildDecorations(
        editor.state.doc,
        mergedRanges,
        after.spellEnabled,
        after.grammarEnabled
      );
      const tr = editor.state.tr.setMeta(grammarPluginKey, {
        type: "recheck-complete",
        decorations,
      });
      editor.view.dispatch(tr);
    }

    function scheduleCheck(
      mode: "full" | "paragraph" = "full",
      cursorPos: number | null = null,
      immediate: boolean = false,
      source: GrammarCheckSource =
        mode === "paragraph" ? "typing" : "manual_recheck"
    ) {
      if (editor.storage.grammar.pendingCheck) {
        clearTimeout(editor.storage.grammar.pendingCheck);
      }

      editor.storage.grammar.pendingCheck = setTimeout(async () => {
        editor.storage.grammar.pendingCheck = null;
        abortGrammarRequests(editor.storage.grammar);
        const mySeq = ++editor.storage.grammar.requestSeq;
        const checkAbort = new AbortController();
        editor.storage.grammar.checkAbort = checkAbort;
        let streamAbort: AbortController | null = null;

        try {
          if (!editor.view || editor.isDestroyed) return;
          const { spellEnabled, grammarEnabled } = editor.storage.grammar;
          if (!spellEnabled && !grammarEnabled) return;

          // Make sure /preferences is loaded before the first /check —
          // otherwise the response would land before we know which
          // matches to filter.
          const preferences = await ensurePreferences();

          const currentDoc = editor.state.doc;

          // Decide the text + segments to send + which slice of stored
          // ranges to replace. For "paragraph" mode we only POST the
          // textblock containing the cursor; for "full" mode we send the
          // whole doc (page load, toggle-on, after Add to dictionary).
          let plainText: string;
          let segments: ReturnType<typeof extractText>["segments"];
          let extractedFullText: ReturnType<typeof extractText> | null = null;
          let replaceRange: { paraStart: number; paraEnd: number } | null = null;

          if (mode === "paragraph" && cursorPos != null) {
            const para = extractParagraphAt(currentDoc, cursorPos);
            if (!para || !para.plainText.trim()) {
              // Nothing to check in this paragraph — clear any old
              // matches that lived inside it and bail.
              const kept = editor.storage.grammar.ranges.filter(
                (r: DecorationRange) =>
                  para
                    ? r.to <= para.paraStart || r.from >= para.paraEnd
                    : true
              );
              editor.storage.grammar.ranges = kept;
              const decorations = buildDecorations(
                currentDoc,
                kept,
                spellEnabled,
                grammarEnabled
              );
              const tr = editor.state.tr.setMeta(grammarPluginKey, {
                type: "recheck-complete",
                decorations,
              });
              editor.view.dispatch(tr);
              return;
            }
            plainText = para.plainText;
            segments = para.segments;
            replaceRange = { paraStart: para.paraStart, paraEnd: para.paraEnd };
          } else {
            const extracted = extractText(currentDoc);
            extractedFullText = extracted;
            plainText = extracted.plainText;
            segments = extracted.segments;
          }

          // SW-9064: language preference is pushed into editor storage by
          // TiptapDocumentSB on mount + on UserData change. Keeps this
          // extension free of a Firestore-loading store import so unit
          // tests can load it without mocking firebase.
          const language: string =
            (editor.storage.grammar.languagePreference as string) || "en-US";

          const checkOptions: GrammarCheckOptions = {
            language,
            styleRules: preferences.styleRules,
            source,
            signal: checkAbort.signal,
          };
          const storyBibleNameTokens = collectStoryBibleNameTokens();
          const dictionary = [
            ...preferences.dictionary,
            ...storyBibleNameTokens,
          ];
          let fullCacheKey: string | null = null;
          let useProgressiveFullCheck = false;
          if (mode === "full" && extractedFullText) {
            fullCacheKey = buildGrammarCheckCacheKey({
              text: plainText,
              language,
              styleRules: preferences.styleRules,
            });

            useProgressiveFullCheck =
              plainText.length >= FULL_DOC_PREVIEW_MIN_CHARS;
          }

          if (useProgressiveFullCheck) {
            streamAbort = new AbortController();
            editor.storage.grammar.streamAbort = streamAbort;
          }
          const fullDocumentCheck = useProgressiveFullCheck
            ? (text: string, options: GrammarCheckOptions) =>
                checkGrammarProgressively(text, options, {
                  signal: streamAbort!.signal,
                  onChunk: (chunk) =>
                    applyFullDocumentChunk({
                      chunk,
                      plainText,
                      segments,
                      preferences,
                      dictionary,
                      possessiveBases: storyBibleNameTokens,
                      language,
                      requestSeq: mySeq,
                      fullCacheKey: fullCacheKey!,
                    }),
                })
            : checkGrammar;

          const response =
            mode === "full"
              ? await checkGrammarWithFullDocumentCache(
                  plainText,
                  checkOptions,
                  {
                    get cache() {
                      return editor.storage.grammar.fullCheckCache;
                    },
                    set cache(value) {
                      editor.storage.grammar.fullCheckCache = value;
                    },
                    get inFlight() {
                      return editor.storage.grammar.fullCheckInFlight;
                    },
                    set inFlight(value) {
                      editor.storage.grammar.fullCheckInFlight = value;
                    },
                  },
                  fullDocumentCheck
                )
              : await checkGrammar(plainText, checkOptions);

          if (mySeq !== editor.storage.grammar.requestSeq) return;
          if (!editor.view || editor.isDestroyed) return;
          if (
            checkAbort &&
            editor.storage.grammar.checkAbort === checkAbort
          ) {
            editor.storage.grammar.checkAbort = null;
          }
          if (
            streamAbort &&
            editor.storage.grammar.streamAbort === streamAbort
          ) {
            editor.storage.grammar.streamAbort = null;
          }
          const after = editor.storage.grammar;
          if (!after.spellEnabled && !after.grammarEnabled) return;

          // Effective dictionary = user's saved words + every Story Bible
          // character name (multi-word names tokenized so LT's single-word
          // matches are also silenced).
          const filtered = filterMatches({
            text: plainText,
            matches: response.matches,
            dictionary,
            possessiveBases: storyBibleNameTokens,
            language,
            styleRules: preferences.styleRules,
          });

          const newRanges = matchesToDecorationRanges(filtered, segments);

          let mergedRanges: DecorationRange[];
          if (replaceRange) {
            // Keep ranges that live entirely outside the paragraph; replace
            // anything that overlapped it with the freshly-checked results.
            const { paraStart, paraEnd } = replaceRange;
            const kept = after.ranges.filter(
              (r: DecorationRange) => r.to <= paraStart || r.from >= paraEnd
            );
            mergedRanges = [...kept, ...newRanges];
          } else {
            mergedRanges = newRanges;
          }

          editor.storage.grammar.ranges = mergedRanges;

          const decorations = buildDecorations(
            editor.state.doc,
            mergedRanges,
            after.spellEnabled,
            after.grammarEnabled
          );

          if (!editor.view || editor.isDestroyed) return;
          const tr = editor.state.tr.setMeta(grammarPluginKey, {
            type: "recheck-complete",
            decorations,
          });
          editor.view.dispatch(tr);
        } catch (err) {
          if (err instanceof Error && err.name === "AbortError") {
            if (
              checkAbort &&
              editor.storage.grammar.checkAbort === checkAbort
            ) {
              editor.storage.grammar.checkAbort = null;
            }
            if (
              streamAbort &&
              editor.storage.grammar.streamAbort === streamAbort
            ) {
              editor.storage.grammar.streamAbort = null;
            }
            return;
          }
          // eslint-disable-next-line no-console
          console.error("[spell+grammar] check failed:", err);
        }
      }, immediate ? 0 : DEBOUNCE_MS);
    }
  },
});

export default GrammarExtension;
